import { Field as FieldType } from '../fieldtypes/Field';
import { Message as MessageType } from '../fieldtypes/Message';
import { GROUPS, type ISpecGroups } from '../spec/SpecGroups';

/**
 * Class for managing FIX protocol groups.
 * Provides functionality to find and filter groups based on various criteria.
 * Uses caching to improve performance when looking up groups.
 */
export class Groups {
    /** Array of all available groups from the FIX specification */
    public groups: ISpecGroups[] = GROUPS;
    /** Cache map for looking up groups by their ID */
    public cacheMap = new Map<number, ISpecGroups>();
    /** Cache map for looking up groups by their name */
    public cacheMapByName = new Map<string, ISpecGroups>();

    /**
     * Creates a new Groups instance and initializes the cache maps.
     * Populates both the ID-based and name-based cache maps for efficient lookups.
     */
    constructor() {
        this.groups.forEach((component) => {
            this.cacheMap.set(component.id, component);
        });
        this.groups.forEach((component) => {
            this.cacheMapByName.set(component.name, component);
        });
    }

    /**
     * Finds a group by its component ID.
     *
     * @param {number} componentId - The ID of the group to find
     * @returns {ISpecGroups | undefined} The found group, or undefined if not found
     */
    public find(componentId: number): ISpecGroups | undefined {
        return this.cacheMap.get(componentId);
    }

    /**
     * Finds a group by its name.
     *
     * @param {string} name - The name of the group to find
     * @returns {ISpecGroups | undefined} The found group, or undefined if not found
     */
    public findByName(name: string): ISpecGroups | undefined {
        return this.cacheMapByName.get(name);
    }

    /**
     * Filters and finds a group based on the NoMDEntries tag and message type.
     * Handles special cases for MarketDataSnapshotFullRefresh and MarketDataIncrementalRefresh messages.
     * Falls back to searching by numInGroup ID if no special case matches.
     *
     * @param {number} tag - The tag number to filter by (typically NoMDEntries)
     * @param {string} messageType - The type of the message being processed
     * @returns {ISpecGroups | undefined} The found group, or undefined if not found
     */
    public filterMapByNumInGroupId(tag: number, messageType: string): ISpecGroups | undefined {
        // If tag === 268 and MessageType W then we have a MDFullGrp (pick group 2031)
        if (tag === FieldType.NoMDEntries && messageType === MessageType.MarketDataSnapshotFullRefresh) {
            return this.find(2031);
        }

        // If tag === 268 and MessageType X then we have a MDIncGrp (pick group 2032)
        if (tag === FieldType.NoMDEntries && messageType === MessageType.MarketDataIncrementalRefresh) {
            return this.find(2032);
        }

        for (const [, value] of this.cacheMap.entries()) {
            if (value.numInGroup?.id === tag) {
                return value;
            }
        }

        return undefined;
    }
}
